import React, { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('cadence_token'));
  const [loading, setLoading] = useState(true);
  const [twoFARequired, setTwoFARequired] = useState(null); // { user_id, email_masked }

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('cadence_token');
      if (storedToken) {
        // Set token immediately to prevent flicker
        setToken(storedToken);
        try {
          const response = await fetch(`${API_URL}/api/auth/me`, {
            headers: {
              'Authorization': `Bearer ${storedToken}`
            }
          });
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
          } else {
            // Only clear if server explicitly rejects
            localStorage.removeItem('cadence_token');
            setToken(null);
            setUser(null);
          }
        } catch (error) {
          // Network error - keep token, user might be offline temporarily
          console.error('Auth check failed (network):', error);
          // Don't clear token on network errors - preserve session
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/auth/login`);
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      xhr.onload = function() {
        let data;
        try {
          data = JSON.parse(xhr.responseText);
        } catch {
          data = { detail: xhr.responseText || 'Unknown error' };
        }
        
        if (xhr.status >= 200 && xhr.status < 300) {
          localStorage.setItem('cadence_token', data.token);
          setToken(data.token);
          setUser(data.user);
          setTwoFARequired(null);
          resolve({ success: true });
        } else if (xhr.status === 202 && data.detail?.requires_2fa) {
          // 2FA required
          setTwoFARequired({
            user_id: data.detail.user_id,
            email_masked: data.detail.email_masked
          });
          resolve({ success: false, requires_2fa: true, email_masked: data.detail.email_masked });
        } else {
          resolve({ success: false, error: data.detail || 'Login failed' });
        }
      };
      
      xhr.onerror = function() {
        resolve({ success: false, error: 'Unable to connect to server' });
      };
      
      xhr.send(JSON.stringify({ email, password }));
    });
  };

  const verify2FA = async (code) => {
    if (!twoFARequired) {
      return { success: false, error: 'No 2FA session active' };
    }
    
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/auth/verify-2fa`);
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      xhr.onload = function() {
        let data;
        try {
          data = JSON.parse(xhr.responseText);
        } catch {
          data = { detail: xhr.responseText || 'Unknown error' };
        }
        
        if (xhr.status >= 200 && xhr.status < 300) {
          localStorage.setItem('cadence_token', data.token);
          setToken(data.token);
          setUser(data.user);
          setTwoFARequired(null);
          resolve({ success: true });
        } else {
          resolve({ success: false, error: data.detail || 'Invalid verification code' });
        }
      };
      
      xhr.onerror = function() {
        resolve({ success: false, error: 'Unable to connect to server' });
      };
      
      xhr.send(JSON.stringify({ user_id: twoFARequired.user_id, code }));
    });
  };

  const verifyBackupCode = async (backupCode) => {
    if (!twoFARequired) {
      return { success: false, error: 'No 2FA session active' };
    }
    
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/auth/2fa/verify-backup-code`);
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      xhr.onload = function() {
        let data;
        try {
          data = JSON.parse(xhr.responseText);
        } catch {
          data = { detail: xhr.responseText || 'Unknown error' };
        }
        
        if (xhr.status >= 200 && xhr.status < 300) {
          localStorage.setItem('cadence_token', data.token);
          setToken(data.token);
          setUser(data.user);
          setTwoFARequired(null);
          resolve({ 
            success: true, 
            backup_codes_remaining: data.backup_codes_remaining,
            warning: data.warning 
          });
        } else {
          resolve({ success: false, error: data.detail || 'Invalid backup code' });
        }
      };
      
      xhr.onerror = function() {
        resolve({ success: false, error: 'Unable to connect to server' });
      };
      
      xhr.send(JSON.stringify({ user_id: twoFARequired.user_id, backup_code: backupCode }));
    });
  };

  const resend2FACode = async () => {
    if (!twoFARequired) {
      return { success: false, error: 'No 2FA session active' };
    }
    
    try {
      const response = await fetch(`${API_URL}/api/auth/resend-2fa-code?user_id=${twoFARequired.user_id}`, {
        method: 'POST'
      });
      const data = await response.json();
      return { success: true, message: data.message };
    } catch (error) {
      return { success: false, error: 'Failed to resend code' };
    }
  };

  const cancel2FA = () => {
    setTwoFARequired(null);
  };

  const signup = async (userData) => {
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/auth/signup`);
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      xhr.onload = function() {
        let data;
        try {
          data = JSON.parse(xhr.responseText);
        } catch {
          data = { detail: xhr.responseText || 'Unknown error' };
        }
        
        if (xhr.status >= 200 && xhr.status < 300) {
          localStorage.setItem('cadence_token', data.token);
          setToken(data.token);
          setUser(data.user);
          resolve({ success: true });
        } else {
          resolve({ success: false, error: data.detail || 'Signup failed' });
        }
      };
      
      xhr.onerror = function() {
        resolve({ success: false, error: 'Unable to connect to server' });
      };
      
      xhr.send(JSON.stringify(userData));
    });
  };

  const logout = () => {
    localStorage.removeItem('cadence_token');
    setToken(null);
    setUser(null);
    setTwoFARequired(null);
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token,
    twoFARequired,
    login,
    signup,
    logout,
    verify2FA,
    verifyBackupCode,
    resend2FACode,
    cancel2FA,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
